import { Typography } from '@mui/material'
import { intl } from '../../../../../../i18n'
import { SearchItemLabelHorizontalContainer } from '../../../../../components/search-forms/search-item-label-container'
import { BlankableDateTermConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/BlankableDateTermConditionItemLabel'
import { DateTimeTermConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/DateTimeTermConditionItemLabel'
import { TextMatchConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/TextMatchConditionItemLabel'
import { ProjectMemberMultiAutocompleteConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/ProjectMemberMultiAutocompleteConditionItemLabel'
import { SprintGroupedConditionItemLabel } from './SprintGroupedConditionItemLabel'
import { TeamMultiAutocompleteConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/TeamMultiAutocompleteConditionItemLabel'
import { TicketTypeCheckboxSelectConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/TicketTypeCheckboxSelectConditionItemLabel'
import { WbsItemPriorityCheckboxSelectConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/WbsItemPriorityCheckboxSelectConditionItemLabel'
import { WbsItemStatusCheckboxSelectConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/WbsItemStatusCheckboxSelectConditionItemLabel'
import { WbsItemTypeCheckboxSelectConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/WbsItemTypeCheckboxSelectConditionItemLabel'
import { WbsItemSearchSearchConditionV2 } from '../../../hooks/useSearchConditionV2'
import { useOnSingleValueChangedGenerator } from './useOnSingleValueChangedGenerator'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTeamSprintGroups } from '../useTeamSprintGroups'
import { TextMatchCondition } from '../../../../../../domain/value-object/WbsItemSearchConditionV2Vo'
import { TagMultiAutocompleteConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/TagMultiAutocompleteConditionItemLabel'
import { colorPalette } from '../../../../../style/colorPalette'
import { ProcessScopeConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/ProcessScopeConditionItemLabel'
import projectMember from '../../../../../../lib/functions/projectMember'
import { useProjectPrivateContext } from '../../../../../context/projectContext'
import { SelectOption } from '../../../../../model/selectOption'

type WbsItemSearchSearchItemLabelsV2Props = {
  searchCondition: WbsItemSearchSearchConditionV2
  isLoading: boolean
  onChange: (updated: Partial<WbsItemSearchSearchConditionV2>) => void
  search: () => void
  onClose: () => void
  teamOptions: SelectOption[]
  processOptions: SelectOption[]
  processParentMap: Record<string, string>
  processOptionsLoading: boolean
  includeDeliverableOptions: boolean
  onToggleIncludeDeliverableOptions: (checked: boolean) => void
  isDeliverableSelected: boolean
  isToggleDisabled: boolean
}

export const WbsItemSearchSearchItemLabelsV2 = ({
  searchCondition,
  isLoading,
  onChange,
  search,
  onClose,
  teamOptions,
  processOptions,
  processParentMap,
  processOptionsLoading,
  includeDeliverableOptions,
  onToggleIncludeDeliverableOptions,
  isDeliverableSelected,
  isToggleDisabled,
}: WbsItemSearchSearchItemLabelsV2Props) => {
  const { projectUuid } = useProjectPrivateContext()
  const { onChangeValueGenerator, onChangeBlankGenerator } =
    useOnSingleValueChangedGenerator(searchCondition, onChange)

  const [memberOptions, setMemberOptions] = useState<SelectOption[]>([])
  useEffect(() => {
    if (!projectUuid) {
      setMemberOptions([])
      return
    }
    const fetchMembers = async () => {
      const projectMembers = await projectMember.getProjectMembers()
      setMemberOptions(
        projectMembers.json.map(pm => ({
          value: pm.user.uuid,
          name: pm.user.name,
          iconUrl: pm.user.iconUrl,
        }))
      )
    }
    fetchMembers()
  }, [projectUuid])

  const { teamSprintGroups, sprintOptions } = useTeamSprintGroups(
    searchCondition.teamUuids,
    teamOptions
  )

  // チーム選択解除時に、そのチームに属するスプリントの選択を解除する
  // sprintOptions が空かつチームが選択されている場合はフェッチ中なのでスキップ
  useEffect(() => {
    const selectedSprintUuids = searchCondition.sprintUuids
    if (!selectedSprintUuids || selectedSprintUuids.length === 0) return
    const hasTeams =
      searchCondition.teamUuids && searchCondition.teamUuids.length > 0
    if (hasTeams && sprintOptions.length === 0) return
    const validUuids = new Set(sprintOptions.map(o => o.value))
    const filtered = selectedSprintUuids.filter(uuid => validUuids.has(uuid))
    if (filtered.length < selectedSprintUuids.length) {
      onChange({ sprintUuids: filtered.length > 0 ? filtered : undefined })
    }
  }, [sprintOptions])

  const onTagChange = useCallback(
    (newValue: TextMatchCondition[] | undefined) => {
      onChange({ tagUuids: newValue })
    },
    [onChange]
  )

  const noSearchCondition = useMemo(() => {
    return Object.values(searchCondition)
      .map(v => {
        if (typeof v === 'undefined') {
          return true
        }
        if (Array.isArray(v)) {
          return v.length === 0
        }
        return !v
      })
      .every(v => v)
  }, [searchCondition])

  return (
    <SearchItemLabelHorizontalContainer>
      {noSearchCondition ? (
        <Typography
          sx={{
            fontSize: '14px',
            color: colorPalette.monotone[5],
            fontWeight: 500,
            marginLeft: '10px',
          }}
        >
          {intl.formatMessage({ id: 'wbs.search.no.search.condition' })}
        </Typography>
      ) : (
        <>
          <ProcessScopeConditionItemLabel
            value={searchCondition.rootUuids}
            onChange={v => onChange({ rootUuids: v })}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
            options={processOptions}
            parentMap={processParentMap}
            optionsLoading={processOptionsLoading}
            includeDeliverableOptions={includeDeliverableOptions}
            onToggleIncludeDeliverableOptions={
              onToggleIncludeDeliverableOptions
            }
            isDeliverableSelected={isDeliverableSelected}
            isToggleDisabled={isToggleDisabled}
          />
          <TextMatchConditionItemLabel
            header={intl.formatMessage({ id: 'wbsItemSearchFilterPanel.code' })}
            value={searchCondition.code}
            onChange={v => onChange({ code: v })}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <WbsItemTypeCheckboxSelectConditionItemLabel
            value={searchCondition.types}
            onChange={onChangeValueGenerator('types')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <TicketTypeCheckboxSelectConditionItemLabel
            value={searchCondition.ticketTypes}
            onChange={onChangeValueGenerator('ticketTypes')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <WbsItemStatusCheckboxSelectConditionItemLabel
            value={searchCondition.status}
            onChange={onChangeValueGenerator('status')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <WbsItemPriorityCheckboxSelectConditionItemLabel
            value={searchCondition.priority}
            onChange={onChangeValueGenerator('priority')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <TextMatchConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.displayName',
            })}
            value={searchCondition.displayName}
            onChange={v => onChange({ displayName: v })}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <TextMatchConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.description',
            })}
            value={searchCondition.description}
            onChange={v => onChange({ description: v })}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <TeamMultiAutocompleteConditionItemLabel
            value={searchCondition.teamUuids}
            onChange={onChangeValueGenerator('teamUuids')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
            teamOptions={teamOptions}
          />
          <SprintGroupedConditionItemLabel
            value={searchCondition.sprintUuids}
            teamSprintGroups={teamSprintGroups}
            sprintOptions={sprintOptions}
            onChange={onChangeValueGenerator('sprintUuids')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <ProjectMemberMultiAutocompleteConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.accountable',
            })}
            value={searchCondition.accountableUuids}
            onChange={onChangeValueGenerator('accountableUuids')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
            memberOptions={memberOptions}
          />
          <ProjectMemberMultiAutocompleteConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.responsible',
            })}
            value={searchCondition.responsibleUuids}
            onChange={onChangeValueGenerator('responsibleUuids')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
            memberOptions={memberOptions}
          />
          <ProjectMemberMultiAutocompleteConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.assignee',
            })}
            value={searchCondition.assigneeUuids}
            onChange={onChangeValueGenerator('assigneeUuids')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
            memberOptions={memberOptions}
          />
          <TagMultiAutocompleteConditionItemLabel
            triState={true}
            value={searchCondition.tagUuids}
            onChange={onTagChange}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <BlankableDateTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.scheduledStartDate',
            })}
            value={searchCondition.scheduledStartDate}
            onChange={onChangeValueGenerator('scheduledStartDate')}
            onClose={onClose}
            search={search}
            blank={!!searchCondition.blank?.scheduledStartDate}
            onChangeBlank={onChangeBlankGenerator('scheduledStartDate')}
            isLoading={isLoading}
          />
          <BlankableDateTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.scheduledEndDate',
            })}
            value={searchCondition.scheduledEndDate}
            onChange={onChangeValueGenerator('scheduledEndDate')}
            onClose={onClose}
            search={search}
            blank={!!searchCondition.blank?.scheduledEndDate}
            onChangeBlank={onChangeBlankGenerator('scheduledEndDate')}
            isLoading={isLoading}
          />
          <BlankableDateTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.actualStartDate',
            })}
            value={searchCondition.actualStartDate}
            onChange={onChangeValueGenerator('actualStartDate')}
            onClose={onClose}
            search={search}
            blank={!!searchCondition.blank?.actualStartDate}
            onChangeBlank={onChangeBlankGenerator('actualStartDate')}
            isLoading={isLoading}
          />
          <BlankableDateTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.actualEndDate',
            })}
            value={searchCondition.actualEndDate}
            onChange={onChangeValueGenerator('actualEndDate')}
            onClose={onClose}
            search={search}
            blank={!!searchCondition.blank?.actualEndDate}
            onChangeBlank={onChangeBlankGenerator('actualEndDate')}
            isLoading={isLoading}
          />
          <DateTimeTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.createdAt',
            })}
            value={searchCondition.createdAt}
            onChange={onChangeValueGenerator('createdAt')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <DateTimeTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.updatedAt',
            })}
            value={searchCondition.updatedAt}
            onChange={onChangeValueGenerator('updatedAt')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <ProjectMemberMultiAutocompleteConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.createdBy',
            })}
            value={searchCondition.createdByUuids}
            onChange={onChangeValueGenerator('createdByUuids')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
            memberOptions={memberOptions}
          />
          <ProjectMemberMultiAutocompleteConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.updatedBy',
            })}
            value={searchCondition.updatedByUuids}
            onChange={onChangeValueGenerator('updatedByUuids')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
            memberOptions={memberOptions}
          />
        </>
      )}
    </SearchItemLabelHorizontalContainer>
  )
}
